import { SecondaryRegularButton } from 'components/ReusableComponents/Button/RegularButtons'
import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import TextInput from 'components/ReusableComponents/ManageHotel/TextInput'
import TemplateCardIllustration from 'features/EmailEditor/TemplateCardIllustration'
import TemplatePickerSidePreview from 'features/EmailEditor/TemplatePickerSidePreview'
import TemplatePreviewTooltip from 'features/EmailEditor/TemplatePreviewTooltip'
import { getTemplateOptions } from 'features/EmailEditor/templates'
import { useEffect, useMemo, useState } from 'react'

import styles from './EmailEditor.module.scss'

const EmailTemplatePicker = ({ selectedTemplate, onTemplateChange }) => {
  const [isPickerOpen, setIsPickerOpen] = useState(false)
  const [pendingTemplateKey, setPendingTemplateKey] = useState(selectedTemplate)
  const [searchQuery, setSearchQuery] = useState('')
  const [hoveredTemplateKey, setHoveredTemplateKey] = useState(null)
  const [hoveredTemplateLabel, setHoveredTemplateLabel] = useState('')
  const [hoverAnchorRect, setHoverAnchorRect] = useState(null)

  useEffect(() => {
    if (!isPickerOpen) {
      setPendingTemplateKey(selectedTemplate)
    }
  }, [isPickerOpen, selectedTemplate])

  const templateOptions = getTemplateOptions()
  const selectedOption =
    templateOptions.find(option => option.value === selectedTemplate) || templateOptions[0]

  const filteredOptions = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase()

    if (!normalizedQuery) {
      return templateOptions
    }

    return templateOptions.filter(option => {
      const haystack = `${option.label} ${option.description} ${option.value}`.toLowerCase()
      return haystack.includes(normalizedQuery)
    })
  }, [searchQuery, templateOptions])

  const modalPreviewKey = hoveredTemplateKey || pendingTemplateKey

  function handleSummaryPreviewEnter(event, templateKey, label) {
    setHoveredTemplateKey(templateKey)
    setHoveredTemplateLabel(label)
    setHoverAnchorRect(event.currentTarget.getBoundingClientRect())
  }

  function handleCardPreviewEnter(templateKey) {
    setHoveredTemplateKey(templateKey)
  }

  function handleGridPreviewLeave() {
    setHoveredTemplateKey(null)
  }

  function handleSummaryPreviewLeave() {
    setHoveredTemplateKey(null)
    setHoveredTemplateLabel('')
    setHoverAnchorRect(null)
  }

  function handleOpenPicker() {
    setPendingTemplateKey(selectedTemplate)
    setHoveredTemplateKey(null)
    setIsPickerOpen(true)
  }

  function handleClosePicker() {
    setIsPickerOpen(false)
    setSearchQuery('')
    setHoveredTemplateKey(null)
    setHoveredTemplateLabel('')
    setHoverAnchorRect(null)
  }

  function handleSelectTemplate(templateKey) {
    setPendingTemplateKey(templateKey)
  }

  function handleApplyTemplate() {
    if (pendingTemplateKey !== selectedTemplate) {
      onTemplateChange(pendingTemplateKey)
    }
    handleClosePicker()
  }

  return (
    <div className={styles.templatePicker}>
      <label className={styles.fieldLabel}>Layout</label>
      <div
        className={styles.templatePickerSummary}
        onMouseEnter={event =>
          handleSummaryPreviewEnter(event, selectedOption.value, selectedOption.label)
        }
        onMouseLeave={handleSummaryPreviewLeave}
      >
        <div className={styles.templatePickerSummaryPreview}>
          <TemplateCardIllustration templateKey={selectedTemplate} compact />
        </div>
        <div className={styles.templatePickerSummaryContent}>
          <span className={styles.templatePickerSummaryName}>{selectedOption.label}</span>
          <span className={styles.templatePickerSummaryDescription}>
            {selectedOption.description}
          </span>
        </div>
        <SecondaryRegularButton className={styles.templatePickerChangeButton} onClick={handleOpenPicker}>
          Change layout
        </SecondaryRegularButton>
      </div>

      {isPickerOpen && (
        <ConfirmationDialog
          title="Choose email layout"
          onClose={handleClosePicker}
          negativeMessage="Close"
          positiveMessage="Use Layout"
          onConfirm={handleApplyTemplate}
          isButtonDisabled={pendingTemplateKey === selectedTemplate}
          maxWidth={1440}
          styleOverrides={{
            width: 'min(96vw, 1440px)',
            maxHeight: 'min(90vh, calc(100vh - 60px))',
            overflow: 'hidden'
          }}
          contentPadding="24px"
          contentStyleOverrides={{
            flex: '1 1 auto',
            minHeight: 0,
            maxHeight: 'none',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box'
          }}
        >
          <div className={styles.templatePickerModalContent}>
            <p className={styles.templatePickerModalIntro}>
              Pick a layout for this design. Hover a card to preview it on the right, then click
              Use Layout to use it. Campaign message text stays on each invite campaign.
            </p>
            <TextInput
              label="Search layouts"
              value={searchQuery}
              onChangeValue={setSearchQuery}
              placeholder="Search by name..."
              autoFocus
            />
            <div className={styles.templatePickerModalBody}>
              <div className={styles.templatePickerModalMain}>
                <div
                  className={styles.templatePickerModalGrid}
                  onMouseLeave={handleGridPreviewLeave}
                >
                  {filteredOptions.map(option => {
                    const isSelected = pendingTemplateKey === option.value
                    const isHovered = hoveredTemplateKey === option.value

                    return (
                      <button
                        key={option.value}
                        type="button"
                        className={[
                          isSelected
                            ? styles.templatePickerModalCardSelected
                            : styles.templatePickerModalCard,
                          isHovered ? styles.templatePickerModalCardHovered : ''
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        onClick={() => handleSelectTemplate(option.value)}
                        onMouseEnter={() => handleCardPreviewEnter(option.value)}
                      >
                        <TemplateCardIllustration templateKey={option.value} />
                        <span className={styles.templatePickerModalCardLabel}>{option.label}</span>
                      </button>
                    )
                  })}
                </div>
                {filteredOptions.length === 0 && (
                  <p className={styles.templatePickerEmptyState}>No layouts match your search.</p>
                )}
              </div>
              <TemplatePickerSidePreview templateKey={modalPreviewKey} />
            </div>
          </div>
        </ConfirmationDialog>
      )}

      {!isPickerOpen && (
        <TemplatePreviewTooltip
          templateKey={hoveredTemplateKey}
          label={hoveredTemplateLabel}
          anchorRect={hoverAnchorRect}
        />
      )}
    </div>
  )
}

export default EmailTemplatePicker
